<template>
    <page-layout>
        <template #header>
            <step-header
                :current-step="stepNumber"
                :total-steps="totalSteps"
                :title="pageTitle"
                :use-exit-modal="false"
                :is-loading="isLoading"
                :skip-steps="isBriefAccepted"
            />
        </template>
        <template #default>
            <g-loader v-if="isLoading" type="fixed" />
            <div v-else>
                <error-message-card v-if="errorMessage" class="margin-bottom-normal" :message="errorMessage" />
                <step-error v-if="step && step.error" class="margin-bottom-normal" :step="step" />

                <g-typography
                    v-if="!showRequiredSharedNetworks && !showOptionalSharedNetworks"
                    tag="h4"
                    :value="stepOverview"
                    class="break-new-line"
                    margins
                />

                <div v-show="!showOptionalSharedNetworks && requiredNetworks.length > 0" class="margin-bottom-large">
                    <div class="margin-bottom-normal">
                        <g-typography
                            v-if="!showRequiredSharedNetworks && !showOptionalSharedNetworks"
                            tag="span"
                            font-weight="semibold"
                        >
                            Required
                        </g-typography>
                    </div>
                    <activation-brief-network-manager
                        :contact="contact"
                        :brief="brief"
                        context="brief"
                        :networks="requiredNetworks"
                        :contact-networks="contact ? contact.networks : null"
                        :tasks="brief.tasks"
                        :readonly="isBriefAccepted"
                        @show-shared-networks="showRequiredSharedNetworksChange"
                        @load-contact="$emit('load-contact')"
                        @meta-connection-drawer-closed="handleMetaConnectionDrawerClosed"
                    />
                </div>

                <div
                    v-show="!showRequiredSharedNetworks && optionalAndTrackedNetworks.length > 0"
                    class="margin-bottom-normal"
                >
                    <div v-if="!showRequiredSharedNetworks && !showOptionalSharedNetworks" class="margin-bottom-normal">
                        <g-typography tag="span" font-weight="semibold">Optional</g-typography>
                    </div>
                    <activation-brief-network-manager
                        :contact="contact"
                        :brief="brief"
                        context="brief"
                        :networks="optionalAndTrackedNetworks"
                        :contact-networks="contact.networks"
                        :tasks="brief.tasks"
                        :readonly="isBriefAccepted"
                        @show-shared-networks="showOptionalSharedNetworksChange"
                        @load-contact="$emit('load-contact')"
                        @meta-connection-drawer-closed="handleMetaConnectionDrawerClosed"
                    />
                </div>
            </div>
        </template>
        <template #footer>
            <step-footer
                v-if="isBriefEditable"
                :is-next-disabled="isNextDisabled"
                :step="step"
                :loading="isCompletingBrief"
                :use-exit-modal="false"
                :step-completed="isCompleted"
                :current-step="stepNumber"
                :total-steps="totalSteps"
                @go-next-step="$emit('go-next-step', step)"
                @go-previous-step="$emit('go-previous-step', step)"
            />
        </template>
    </page-layout>
</template>

<script>
    import { get, uniqBy } from 'lodash';
    import { getCustomizableText } from '../../../helpers/activations/default-texts';
    import { NETWORK_INFO_BY_ID } from '../../../constants/networks';
    import StepFooter from './partials/StepFooter.vue';
    import StepHeader from './partials/StepHeader.vue';
    import StepError from './partials/StepError.vue';
    import PageLayout from '../../layouts/PageLayout.vue';
    import ErrorMessageCard from './partials/ErrorMessageCard.vue';
    import ActivationBriefNetworkManager from './partials/networks/ActivationBriefNetworkManager.vue';
    import ActivationBriefMixin from './mixins/ActivationBriefMixin';
    import ActivationBriefNetworkConnectionSourceMixin from '../../providers/mixins/ActivationBriefNetworkConnectionSourceMixin';
    import ContactMixin from '../../../../src/mixins/ContactMixin';
    import ActivationBriefNetworksMixin from './mixins/ActivationBriefNetworksMixin';
    import ActivationTasksMixin from './mixins/ActivationTasksMixin';
    import useNetworks from '../../../../src/composables/use-networks';

    export default {
        name: 'ActivationBriefNetworksView',
        components: {
            StepFooter,
            StepHeader,
            StepError,
            ErrorMessageCard,
            PageLayout,
            ActivationBriefNetworkManager
        },
        mixins: [
            ActivationBriefMixin,
            ActivationBriefNetworkConnectionSourceMixin,
            ContactMixin,
            ActivationBriefNetworksMixin,
            ActivationTasksMixin
        ],
        props: {
            brief: {
                type: Object,
                default: null
            },
            contact: {
                type: Object,
                default: null
            },
            step: {
                type: Object,
                default: null
            },
            isLoading: {
                type: Boolean,
                default: false
            },
            isCompletingBrief: {
                type: Boolean,
                default: false
            },
            errorMessage: {
                type: String,
                default: ''
            }
        },
        data() {
            return {
                showRequiredSharedNetworks: false,
                showOptionalSharedNetworks: false
            };
        },
        computed: {
            pageTitle() {
                return 'Networks';
            },
            stepOverview() {
                return getCustomizableText(this.brief, 'live_connect_networks_description');
            },
            stepNumber() {
                return get(this, 'step.meta.stepNumber', 0);
            },
            totalSteps() {
                return get(this, 'step.meta.totalSteps', 0);
            },
            isCompleted() {
                return get(this, 'step.is_complete', false);
            },
            isNextDisabled() {
                return !!this.requiredNetworks.find((requiredNetwork) => !this.isConnected(requiredNetwork));
            },
            contentTrackingNetworks() {
                if (!this.brief || !get(this, 'brief.content_tracking_networks', false)) {
                    return [];
                }

                return this.brief.content_tracking_networks.map((networkId) => NETWORK_INFO_BY_ID[networkId]);
            },
            optionalAndTrackedNetworks() {
                return uniqBy(this.optionalNetworks.concat(this.contentTrackingNetworks), 'id');
            }
        },
        methods: {
            isConnected(network) {
                if (useNetworks().isInstagramGraphRequiredForActivationTasks(this.requiredTasks)) {
                    return useNetworks().isConnectedByIdFromContactNetworks(network.id, this.contact.networks);
                }

                return !!useNetworks().getContactNetworkByIdFromContactNetworks(network.id, this.contact.networks);
            },
            showRequiredSharedNetworksChange(value) {
                this.showRequiredSharedNetworks = value;
                this.showOptionalSharedNetworks = false;
            },
            showOptionalSharedNetworksChange(value) {
                this.showOptionalSharedNetworks = value;
                this.showRequiredSharedNetworks = false;
            },
            handleMetaConnectionDrawerClosed() {
                this.showOptionalSharedNetworks = false;
                this.showRequiredSharedNetworks = false;
            }
        }
    };
</script>
